import { Box, Button } from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { AsyncSelectValue } from "../../../../../../components/AsyncSelect";
import CustomSelect from "../../../../../../components/CustomSelect";
import Flex from "../../../../../../components/Flex";
import GlobalFilter from "../../../../../../components/GlobalFilter";
import { MaskMultiSelectScroll } from "../../../../../../components/MaskMultiSelectScroll";
import Text from "../../../../../../components/Text";
import { useDebounce } from "../../../../../../hooks/use-debounce";
import { useSpecialtiesList } from "../../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../utils/utils";
import { TEMPLATE_TYPE_LIST } from "../../form-builder/form-builder-constant";
import { useActiveComponentCountController } from "../../../../../../components/filter/use-ActiveComponentCountController";

interface TemplateFilterProps {
  onApply: (v: any) => void;
  filters: TemplateFilterOptions;
}

export type TemplateFilterOptions = {
  templateType: string;
  specialties?: AsyncSelectValue[];
};

export const templateFilterInitialValues: TemplateFilterOptions = {
  templateType: "",
  specialties: undefined,
};
const TemplateFilter = ({ onApply, filters }: TemplateFilterProps) => {
  const formik = useFormik<TemplateFilterOptions>({
    enableReinitialize: true,
    initialValues: filters,
    onSubmit: (v) => {
      onApply(v as TemplateFilterOptions);
    },
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchSpecialty, setSearchSpecialty] = useDebounce<string>("");
  const [specialtyPagination, setSpecialtyPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: specialtiesList, isFetching: isSpecialtiesFetching } =
    useSpecialtiesList({
      searchString: searchSpecialty,
      xTENANTID: getXtenantId(),
      enabled: !!anchorEl,
      pagination: specialtyPagination,
    });

  const specialities = useMemo(() => {
    return specialtiesList?.data?.content?.map((it) => ({
      value: it.uuid,
      label: it?.name,
    }));
  }, [specialtiesList]);

  const [expanded, setExpanded] = useState<{ [k: string]: boolean }[]>(
    Object.keys(formik.values).map((it) => ({ [it]: false }))
  );

  function handleExpandClick(k: string, value: boolean) {
    setExpanded((prev) =>
      prev?.map((it) => {
        const curKey = Object.keys(it)[0];

        return Object.keys(it).some((obj) => obj === k)
          ? { [k]: value }
          : { [curKey]: false };
      })
    );
  }

  const closeFilter = () => setAnchorEl(null);
  const activeFilterCount = useActiveComponentCountController(formik.values);

  return (
    <GlobalFilter
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      activeFilterCount={activeFilterCount}
    >
      <Flex
        width={"18.438rem"}
        flexDirection={"column"}
        justifyContent={"space-between"}
      >
        <Box>
          <Flex width={"100%"} justifyContent={"end"}>
            <Button
              variant="text"
              onClick={() => {
                onApply(templateFilterInitialValues);
                closeFilter();
              }}
            >
              Clear Filter
            </Button>
          </Flex>
          {Object.keys(formik.values).map((key, ind) =>
            key === "templateType" ? (
              <Accordion
                key={key}
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>Template Type</Text>
                </AccordionSummary>
                <AccordionDetails>
                  <CustomSelect
                    value={formik.values?.templateType}
                    placeholder="Template type"
                    options={TEMPLATE_TYPE_LIST}
                    onChange={(val) =>
                      formik.setFieldValue("templateType", val)
                    }
                  />
                </AccordionDetails>
              </Accordion>
            ) : (
              <Accordion
                key={key}
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{replaceUnderScoreAddSpace(key)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <MaskMultiSelectScroll
                    onSearch={(val) => {
                      setSearchSpecialty(val);
                      setSpecialtyPagination((p) => ({ ...p, page: 0 }));
                    }}
                    optionWithCheckbox={true}
                    value={formik.values?.specialties}
                    placeholder="Search & Select Specialities"
                    options={specialities}
                    onChange={(val) => formik.setFieldValue("specialties", val)}
                    pagination={specialtyPagination}
                    setPagination={setSpecialtyPagination}
                    totalElements={specialtiesList?.data?.page?.totalElements}
                    loading={isSpecialtiesFetching}
                  />
                </AccordionDetails>
              </Accordion>
            )
          )}
        </Box>
        <Flex width={"100%"} justifyContent={"end"}>
          <Button
            variant="text"
            sx={{ color: "gray" }}
            onClick={() => {
              closeFilter();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={() => {
              formik.submitForm();
              closeFilter();
            }}
          >
            Apply
          </Button>
        </Flex>
      </Flex>
    </GlobalFilter>
  );
};

export default TemplateFilter;
